import { useMemo, type CSSProperties } from 'react';
import { ChoicesSelect } from './ChoicesSelect';

export const adminPdfActionButtonClassName = 'btn btn-outline admin-pdf-export-btn report-action-btn';
export const adminPdfActionButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  height: 48,
  padding: '12px 22px',
  borderRadius: 12,
  fontWeight: 700,
  marginLeft: 8,
  border: '1px solid var(--primary)',
  color: 'var(--primary)',
  background: 'transparent',
  boxShadow: 'none',
} satisfies CSSProperties;

export function AdminPdfActionIcon({ kind }: { kind: 'definitions' | 'download' }) {
  const iconProps = {
    'aria-hidden': true,
    width: 16,
    height: 16,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  if (kind === 'download') return <svg {...iconProps}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>;
  return <svg {...iconProps}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" /><path d="M8 7h8M8 11h6" /></svg>;
}

export type AdminModalVariant = 'success' | 'error' | 'warning' | 'info';

const modalStyles: Record<AdminModalVariant, { title: string; color: string; background: string; icon: string }> = {
  success: { title: 'Success', color: '#10b981', background: 'rgba(16, 185, 129, 0.12)', icon: '✓' },
  error: { title: 'Alert', color: '#ef4444', background: 'rgba(239, 68, 68, 0.12)', icon: '!' },
  warning: { title: 'Warning', color: '#f59e0b', background: 'rgba(245, 158, 11, 0.14)', icon: '!' },
  info: { title: 'Information', color: 'var(--primary)', background: 'var(--primary-light)', icon: 'i' },
};

export function Loader({ message = 'Processing your request...', onClose }: { message?: string; onClose?: () => void }) {
  return (
    <div className="loader" id="loader" style={{ display: 'flex' }}>
      {onClose && <button className="loader-close" onClick={onClose} title="Cancel and go back">x</button>}
      <div className="spinner" />
      <div style={{ marginTop: 24, fontWeight: 600, color: 'var(--text-muted)' }}>{message}</div>
    </div>
  );
}

export function SelectGroup({ label, id, value, options, labels, allLabel, onChange }: { label: string; id: string; value: string; options: string[]; labels?: Record<string, string>; allLabel?: string; onChange: (value: string) => void }) {
  return (
    <div className="input-group">
      <label htmlFor={id}>{label}</label>
      <ChoicesSelect
        id={id}
        value={value}
        onChange={onChange}
        searchEnabled={false}
        options={options.map(option => ({
          value: option,
          label: option === 'ALL' && allLabel ? allLabel : labels?.[option] || option,
        }))}
      />
    </div>
  );
}

export function Kpi({ label, id, value }: { label: string; id: string; value: string | number }) {
  return <div className="kpi-card"><div><div className="kpi-label">{label}</div><div className="kpi-val" id={id}>{value}</div></div><div className="kpi-subtext">-</div></div>;
}

export function SimpleTable({ rows, empty, actions }: { rows: object[]; empty: string; actions?: (row: Record<string, unknown>) => React.ReactNode }) {
  const tableRows = rows as Record<string, unknown>[];
  const headers = useMemo(() => Array.from(new Set(tableRows.flatMap(row => Object.keys(row).slice(0, 8)))).slice(0, 8), [tableRows]);
  if (!rows.length) return <div style={{ padding: 40, color: 'var(--text-muted)' }}>{empty}</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table>
        <thead><tr>{headers.map(header => <th key={header}>{header}</th>)}{actions && <th>Actions</th>}</tr></thead>
        <tbody>{tableRows.map((row, index) => <tr key={String(row.id ?? index)}>{headers.map(header => <td key={header}>{typeof row[header] === 'object' ? JSON.stringify(row[header]) : String(row[header] ?? '-')}</td>)}{actions && <td>{actions(row)}</td>}</tr>)}</tbody>
      </table>
    </div>
  );
}

export function AdminAlertModal({ message, type = 'error', title, onClose }: { message: string; type?: AdminModalVariant; title?: string; onClose: () => void }) {
  const style = modalStyles[type];
  return (
    <div className="modal-overlay" style={{ display: 'flex', position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(8px)', zIndex: 99999, alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'opacity 0.3s ease' }}>
      <div className="modal-content admin-premium-modal" style={{ background: 'var(--surface-solid)', border: '1px solid var(--border)', borderRadius: 20, width: '90%', maxWidth: 400, padding: 28, boxShadow: 'var(--shadow-lg)', transform: 'scale(1)', transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)', position: 'relative', textAlign: 'center' }}>
        <button onClick={onClose} aria-label="Close alert" className="admin-modal-close">&times;</button>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 8 }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20, background: style.background, color: style.color, fontSize: 28, fontWeight: 900 }}>{style.icon}</div>
          <h3 style={{ margin: '0 0 12px', fontFamily: 'Outfit, sans-serif', fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>{title || style.title}</h3>
          <p style={{ margin: '0 0 24px', color: 'var(--text-muted)', fontSize: 14.5, lineHeight: 1.6 }}>{message}</p>
          <button className="btn btn-primary" onClick={onClose} style={{ width: '100%', height: 48, borderRadius: 12, fontWeight: 700, fontSize: 13.5, boxShadow: '0 4px 12px var(--primary-glow)' }}>Okay</button>
        </div>
      </div>
    </div>
  );
}

export function AdminConfirmModal({ message, title = 'Confirm Action', onConfirm, onCancel }: { message: string; title?: string; onConfirm: () => void; onCancel: () => void }) {
  return (
    <div className="modal-overlay" style={{ display: 'flex', position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(8px)', zIndex: 99999, alignItems: 'center', justifyContent: 'center', opacity: 1, transition: 'opacity 0.3s ease' }}>
      <div className="modal-content admin-premium-modal" style={{ background: 'var(--surface-solid)', border: '1px solid var(--border)', borderRadius: 20, width: '90%', maxWidth: 400, padding: 28, boxShadow: 'var(--shadow-lg)', transform: 'scale(1)', transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)', position: 'relative', textAlign: 'center' }}>
        <button onClick={onCancel} aria-label="Close confirmation" className="admin-modal-close">&times;</button>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 8 }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20, background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444', fontSize: 28, fontWeight: 900 }}>!</div>
          <h3 style={{ margin: '0 0 12px', fontFamily: 'Outfit, sans-serif', fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>{title}</h3>
          <p style={{ margin: '0 0 24px', color: 'var(--text-muted)', fontSize: 14.5, lineHeight: 1.6 }}>{message}</p>
          <div style={{ display: 'flex', width: '100%', gap: 12 }}>
            <button className="btn btn-outline" onClick={onCancel} style={{ flex: 1, height: 48, borderRadius: 12, fontWeight: 700, fontSize: 13.5 }}>Cancel</button>
            <button className="btn btn-primary" onClick={onConfirm} style={{ flex: 1, height: 48, borderRadius: 12, fontWeight: 700, fontSize: 13.5, background: '#ef4444', boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)' }}>Confirm</button>
          </div>
        </div>
      </div>
    </div>
  );
}
